/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CheckYourEmail } from '@com-delta-idp/mnx-login-ui';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { CheckEmailAdapterService } from '../../adapters/check-email-adapter/check-email-adapter.service';
import { PingLoginAdapterService } from '../../adapters/ping-login-adapter.service';
import {
  AEM_EMAIL,
  STANDALONE_PAGE_URL,
  VIEW_NAME
} from '../../constants/login-constant';
import { LoginModalContent, ResetPasswordFormValues } from '../../models/aem';
import { PingOauthService } from '../../services';
import { LoginContentService } from '../../services/login-content.service';
import { NextDisplayViewService } from '../../services/next-display-view.service';
import { FacadeService } from '../facade.service';
import { RecoverSQAFacadeService } from '../recover-sqa-facade/recover-sqa-facade.service';

@Injectable({
  providedIn: 'root'
})
export class CheckEmailFacadeService {
  pageContent$ = new BehaviorSubject<CheckYourEmail | undefined>(undefined);
  aemContent!: LoginModalContent;
  unsubscribe = new Subject();
  newResetPasswordFlow = false;
  newForgotLoginFlow = false;
  resetLoginPasswordFormValues$ = new BehaviorSubject<
    ResetPasswordFormValues | undefined
  >(undefined);

  facade = inject(FacadeService);
  pingAdapter = inject(PingLoginAdapterService);

  constructor(
    private loginContentService: LoginContentService,
    private checkEmailAdapterService: CheckEmailAdapterService,
    private nextDisplayViewService: NextDisplayViewService,
    private loginAuthFacade: FacadeService,
    private router: Router,
    private pingOauthService: PingOauthService,
    private recoverSQAFacadeService: RecoverSQAFacadeService
  ) {
    this.getCheckEmailPageContent();
    this.getResetPasswordFormValues();
  }

  getCheckEmailPageContent() {
    this.loginContentService
      .getAEMContent()
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((aem) =>
          this.pingOauthService
            .getAuthConfigContent()
            .pipe(map((authConfig) => ({ aem, config: authConfig })))
        )
      )
      .subscribe((response) => {
        this.aemContent = response.aem;
        this.newResetPasswordFlow =
          response.config?.NEW_RESET_PASSWORD_FLOW ?? false;
        this.pageContent$.next(
          this.checkEmailAdapterService.getCheckEmailScreenContent(
            this.aemContent,
            this.newResetPasswordFlow,
            this.newForgotLoginFlow
          )
        );
      });
  }

  getResetPasswordFormValues() {
    this.resetLoginPasswordFormValues$.subscribe(
      (values: ResetPasswordFormValues | undefined) => {
        if (values && Object.values(values).length > 0) {
          const formattedContent =
            this.checkEmailAdapterService.formatCheckEmailMessage(
              this.getContent(),
              values
            );
          this.pageContent$.next(formattedContent);
        }
      }
    );
  }

  getContent(): CheckYourEmail {
    return this.pageContent$.value || ({} as CheckYourEmail);
  }

  replaceBffEmail(bffEmail: string) {
    const { checkEmailMessage_Login: aemMessage } =
      this.aemContent.commonOverlayMessages;
    const aemEmail = AEM_EMAIL;
    const newAemMessage = aemMessage?.replace(aemEmail, bffEmail);
    return newAemMessage;
  }

  displayNextScreen(viewName: string): void {
    if (viewName === VIEW_NAME.SECURITY_QUESTION_ANSWER_SCREEN) {
      this.displayRecoverSqaScreen();
    } else {
      this.loginAuthFacade.getContent().errorMessage = this.newResetPasswordFlow
        ? this.getContent().nextDisplayView.overlayMessage
        : this.getContent().checkEmailMessage;
      this.loginAuthFacade.getContent().loginFormAttributes.formAlertSuccessIcon =
        true;
      this.loginAuthFacade.getContent().errorCode =
        this.loginAuthFacade.getContent().errorMessage;
      this.routeBasedOnPosition(viewName);
    }
  }

  displayRecoverSqaScreen() {
    this.getContent().disabledActionButton = true;
    this.recoverSQAFacadeService.getSQADetails();
    this.recoverSQAFacadeService.sqaError$.subscribe((response) => {
      this.getContent().errorMessage = response;
      this.getContent().hasError = response ? true : false;
      this.getContent().disabledActionButton = false;
    });
  }

  routeBasedOnPositionHelper(viewName: string): void {
    if (
      !this.facade.flowIdForTheTransaction &&
      viewName === VIEW_NAME.LOGIN_CREDENTIALS_SCREEN
    ) {
      this.pingOauthService.getAuthConfigContent().subscribe(async (res) => {
        await this.pingAdapter.processPingAutenticationStarts(res);
      });
    }
  }

  routeBasedOnPosition(viewName: string) {
    if (!this.loginAuthFacade.standalonePage) {
      this.routeBasedOnPositionHelper(viewName);
      this.nextDisplayViewService.nextDisplayView(viewName);
    } else {
      this.routeBasedOnPositionHelper(viewName);
      this.router.navigateByUrl(`/${STANDALONE_PAGE_URL[viewName]}`);
    }
  }

  resetEmailMessage = () => {
    this.pageContent$.next(
      this.checkEmailAdapterService.getCheckEmailScreenContent(
        this.aemContent,
        this.newResetPasswordFlow
      )
    );
  };

  showHideOverylayMessage(value: boolean) {
    this.getContent().hasError = value;
  }

  resetErrors() {
    /* the below code resets the errors set to check email page */
    this.recoverSQAFacadeService.sqaError$.next('');
  }
}
